import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../../lib/supabase';
import { sendFollowUpWA } from '../../../lib/fonnte';
import { getLoyaltySettings, expireStalePoints } from '../../../lib/loyalty';

// ─── Helpers ──────────────────────────────────────────────────────────────────
function daysSince(d) { return d ? Math.floor((Date.now() - new Date(d)) / 86400000) : 999; }
function fmtDate(d)   { return d ? new Date(d).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' }) : '-'; }
function fmtDT(d)     { return d ? new Date(d).toLocaleString('id-ID', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-'; }
const fmtRp = n => 'Rp ' + (n || 0).toLocaleString('id-ID');

function getRetentionTag(c) {
  const ref  = c.last_seen_at || c.created_at;
  const days = daysSince(ref);
  if (days <= 7)  return { label: 'Aktif',         bg: '#E8F5E9', color: '#1B5E20' };
  if (days <= 30) return { label: 'Normal',         bg: '#E3F2FD', color: '#0D47A1' };
  if (days <= 60) return { label: 'Mulai jarang',   bg: '#FFF3E0', color: '#E65100' };
  return             { label: 'Perlu follow-up', bg: '#FFEBEE', color: '#C62828' };
}

const TIER_COLOR = {
  Kenalan:  { bg: '#F5F5F5',           c: '#757575' },
  Langganan:{ bg: '#E3F2FD',           c: '#0D47A1' },
  Sahabat:  { bg: 'rgba(201,146,42,.12)', c: 'var(--gold)' },
  Keluarga: { bg: '#FCE4EC',           c: '#C2185B' },
};

const POINT_TYPE = {
  earn:     { label: 'Transaksi', bg: '#E8F5E9', c: '#1B5E20', prefix: '+' },
  redeem:   { label: 'Redeem',    bg: '#FFF3E0', c: '#E65100', prefix: '-' },
  welcome:  { label: 'Welcome',   bg: '#FFF8E7', c: '#856404', prefix: '+' },
  birthday: { label: 'Birthday',  bg: '#FCE4EC', c: '#C2185B', prefix: '+' },
  adjust:   { label: 'Adjust',    bg: '#F3E5F5', c: '#6A1B9A', prefix: '±' },
  expire:   { label: 'Expire',    bg: '#FFEBEE', c: '#C62828', prefix: '-' },
};

const S = {
  card:  { background: 'white', borderRadius: 'var(--radius)', border: '1px solid var(--border)', boxShadow: 'var(--shadow)' },
  mono:  { fontFamily: "'DM Mono',monospace" },
  serif: { fontFamily: "'Baloo 2',sans-serif" },
  muted: { color: 'var(--muted)', fontSize: '.78rem' },
};

const css = `
  @media (max-width: 640px) {
    .cust-table { display: none !important; }
    .cust-cards { display: block !important; }
  }
  @media (min-width: 641px) {
    .cust-cards { display: none !important; }
  }
  .cust-row { cursor: pointer; transition: background .1s; }
  .cust-row:hover { background: #FDFAF6 !important; }
  .drawer-overlay {
    position: fixed; inset: 0; background: rgba(0,0,0,.45);
    z-index: 400; display: flex; justify-content: flex-end;
  }
  .drawer {
    width: min(480px, 100vw); height: 100vh; overflow-y: auto;
    background: #F7F2EA; box-shadow: -4px 0 24px rgba(0,0,0,.15);
    animation: slideIn .22s ease;
  }
  @keyframes slideIn { from { transform: translateX(100%); } to { transform: translateX(0); } }
`;

// ─── Customer Detail Drawer ───────────────────────────────────────────────────
function CustomerDrawer({ customer, orderCounts, onClose }) {
  const [loyaltyTxs, setLoyaltyTxs] = useState([]);
  const [poOrders,   setPoOrders]   = useState([]);
  const [posOrders,  setPosOrders]  = useState([]);
  const [tab,        setTab]        = useState('poin');
  const [loading,    setLoading]    = useState(true);

  useEffect(() => {
    loadDetail();
  }, [customer.id]);

  async function loadDetail() {
    setLoading(true);
    const [loyRes, poRes, posRes] = await Promise.all([
      supabase
        .from('loyalty_transactions')
        .select('*')
        .eq('customer_id', customer.id)
        .order('created_at', { ascending: false })
        .limit(50),
      supabase
        .from('po_orders')
        .select('id, qty, total, status, payment_method, payment_status, created_at, po:po_batches(date, product:products(name), area:areas(name))')
        .eq('customer_id', customer.id)
        .order('created_at', { ascending: false })
        .limit(30),
      supabase
        .from('pos_orders')
        .select('id, total, status, payment, created_at, order_items(display_name, qty)')
        .eq('customer_id', customer.id)
        .order('created_at', { ascending: false })
        .limit(30),
    ]);
    setLoyaltyTxs(loyRes.data || []);
    setPoOrders(poRes.data || []);
    setPosOrders(posRes.data || []);
    setLoading(false);
  }

  const tag        = getRetentionTag(customer);
  const tierStyle  = TIER_COLOR[customer.loyalty_tier] || TIER_COLOR.Kenalan;
  const totalOrder = (orderCounts[customer.id] || 0);
  const poTotal    = poOrders.filter(o =>
    o.status !== 'batal' &&
    (o.payment_method === 'cod' || (o.payment_method === 'non-tunai' && o.payment_status === 'paid'))
  ).reduce((s, o) => s + o.total, 0);
  const posTotal   = posOrders.filter(o => o.status === 'Selesai').reduce((s, o) => s + o.total, 0);

  const PO_STATUS  = { pending: '#E65100', confirmed: '#0D47A1', paid: '#1B5E20', batal: '#C62828' };
  const POS_STATUS = { New: '#0D47A1', Preparing: '#E65100', Ready: '#1B5E20', Selesai: '#555', Batal: '#C62828' };

  return (
    <div className="drawer-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="drawer">
        {/* Header */}
        <div style={{ background: 'var(--primary-soft)', padding: '18px 20px', position: 'sticky', top: 0, zIndex: 10, borderBottom: '1px solid var(--line)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
            <div>
              <div style={{ ...S.serif, fontSize: '1.2rem', color: 'var(--ink)' }}>{customer.name}</div>
              <a href={`https://wa.me/62${(customer.wa || '').replace(/^0/, '')}`}
                target="_blank" rel="noreferrer"
                style={{ ...S.mono, fontSize: '.72rem', color: '#1B5E20' }}>{customer.wa}</a>
            </div>
            <button onClick={onClose} style={{ background: 'white', border: '1px solid var(--line)', borderRadius: 8, color: 'var(--ink-soft)', padding: '6px 12px', cursor: 'pointer', fontSize: '.8rem' }}>✕</button>
          </div>

          {/* KPI strip */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 8 }}>
            {[
              ['Tier',       <span style={{ ...S.mono, fontSize: '.78rem', background: tierStyle.bg, color: tierStyle.c, borderRadius: 20, padding: '2px 8px' }}>{customer.loyalty_tier}</span>],
              ['Poin',       <span style={{ ...S.serif, fontSize: '1rem', color: 'var(--primary-hover)' }}>{(customer.loyalty_points || 0).toLocaleString('id-ID')}</span>],
              ['Visit',      <span style={{ ...S.serif, fontSize: '1rem', color: 'var(--ink)' }}>{customer.visit_count || 0}×</span>],
            ].map(([label, val]) => (
              <div key={label} style={{ background: 'white', border: '1px solid var(--line)', borderRadius: 8, padding: '8px 10px', textAlign: 'center' }}>
                <div style={{ marginBottom: 4 }}>{val}</div>
                <div style={{ ...S.mono, fontSize: '.58rem', letterSpacing: '.8px', textTransform: 'uppercase', color: 'var(--ink-soft)' }}>{label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Info row */}
        <div style={{ padding: '12px 20px', background: 'white', borderBottom: '1px solid var(--border)', display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: '.76rem', color: 'var(--muted)' }}>
          <span>📅 Sejak {fmtDate(customer.created_at)}</span>
          <span>🕐 Terakhir {fmtDate(customer.last_seen_at || customer.created_at)}</span>
          <span style={{ background: tag.bg, color: tag.color, borderRadius: 20, padding: '1px 8px', ...S.mono, fontSize: '.66rem' }}>{tag.label}</span>
          {customer.source && <span style={{ ...S.mono, fontSize: '.66rem', background: '#F5F5F5', color: '#757575', borderRadius: 20, padding: '1px 8px' }}>{customer.source}</span>}
        </div>

        {/* Omzet strip */}
        <div style={{ padding: '10px 20px', background: '#FDFAF6', borderBottom: '1px solid var(--border)', display: 'flex', gap: 20 }}>
          {[
            ['Lifetime Poin', (customer.loyalty_lifetime || 0).toLocaleString('id-ID') + ' pts'],
            ['Omzet PO',      fmtRp(poTotal)],
            ['Omzet POS',     fmtRp(posTotal)],
          ].map(([l, v]) => (
            <div key={l}>
              <div style={{ ...S.mono, fontSize: '.58rem', letterSpacing: '.8px', textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 2 }}>{l}</div>
              <div style={{ ...S.serif, fontSize: '.95rem' }}>{v}</div>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', borderBottom: '2px solid var(--border)', background: 'white', position: 'sticky', top: 128, zIndex: 9 }}>
          {[
            ['poin',  `Riwayat Poin (${loyaltyTxs.length})`],
            ['po',    `Order PO (${poOrders.length})`],
            ['pos',   `Order POS (${posOrders.length})`],
          ].map(([key, label]) => (
            <button key={key} onClick={() => setTab(key)} style={{
              flex: 1, padding: '10px 4px', background: 'none', border: 'none',
              borderBottom: tab === key ? '2px solid var(--gold)' : '2px solid transparent',
              marginBottom: -2, color: tab === key ? 'var(--dark)' : 'var(--muted)',
              fontWeight: tab === key ? 600 : 400, fontSize: '.75rem', cursor: 'pointer',
              fontFamily: 'inherit', transition: 'color .15s',
            }}>{label}</button>
          ))}
        </div>

        {/* Tab content */}
        <div style={{ padding: '16px 20px 40px' }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: 32, color: 'var(--muted)' }}>Memuat...</div>
          ) : (
            <>
              {/* ── Riwayat Poin ── */}
              {tab === 'poin' && (
                loyaltyTxs.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '28px 0', ...S.muted }}>Belum ada riwayat poin.</div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {loyaltyTxs.map(tx => {
                      const pt = POINT_TYPE[tx.type] || POINT_TYPE.adjust;
                      return (
                        <div key={tx.id} style={{ ...S.card, padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 12 }}>
                          <div style={{ width: 36, height: 36, borderRadius: 10, background: pt.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            <span style={{ ...S.mono, fontSize: '.7rem', color: pt.c, fontWeight: 700 }}>{pt.prefix}{tx.points}</span>
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 2 }}>
                              <span style={{ fontSize: '.83rem', fontWeight: 500 }}>{tx.note || pt.label}</span>
                              <span style={{ ...S.mono, fontSize: '.6rem', background: pt.bg, color: pt.c, borderRadius: 20, padding: '1px 6px' }}>{pt.label}</span>
                            </div>
                            <div style={{ fontSize: '.7rem', color: 'var(--muted)' }}>
                              {fmtDT(tx.created_at)}
                              {tx.order_type && <span style={{ marginLeft: 6, ...S.mono, fontSize: '.62rem', background: '#F5F5F5', borderRadius: 10, padding: '1px 6px' }}>{tx.order_type.toUpperCase()}</span>}
                            </div>
                          </div>
                          <div style={{ ...S.serif, fontSize: '1rem', color: pt.c, flexShrink: 0, fontWeight: 700 }}>
                            {pt.prefix}{tx.points}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )
              )}

              {/* ── Order PO ── */}
              {tab === 'po' && (
                poOrders.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '28px 0', ...S.muted }}>Belum ada order PO.</div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {poOrders.map(o => (
                      <div key={o.id} style={{ ...S.card, padding: '10px 14px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4 }}>
                          <div style={{ fontSize: '.83rem', fontWeight: 500 }}>{o.po?.product?.name || 'Produk PO'}</div>
                          <span style={{ ...S.mono, fontSize: '.62rem', background: PO_STATUS[o.status] ? '#F5F5F5' : '#F5F5F5', color: PO_STATUS[o.status] || '#555', borderRadius: 20, padding: '1px 8px', border: `1px solid ${PO_STATUS[o.status] || '#DDD'}20` }}>{o.status}</span>
                        </div>
                        <div style={{ fontSize: '.73rem', color: 'var(--muted)', display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                          <span>📍 {o.po?.area?.name || '-'}</span>
                          <span>📦 {o.qty} pack</span>
                          <span>📅 {fmtDate(o.po?.date)}</span>
                        </div>
                        <div style={{ ...S.serif, fontSize: '.95rem', color: 'var(--brown)', marginTop: 4 }}>{fmtRp(o.total)}</div>
                      </div>
                    ))}
                  </div>
                )
              )}

              {/* ── Order POS ── */}
              {tab === 'pos' && (
                posOrders.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '28px 0', ...S.muted }}>Belum ada order POS.</div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {posOrders.map(o => (
                      <div key={o.id} style={{ ...S.card, padding: '10px 14px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4 }}>
                          <div style={{ ...S.mono, fontSize: '.68rem', color: 'var(--muted)' }}>{o.id?.slice(0, 18)}...</div>
                          <span style={{ ...S.mono, fontSize: '.62rem', color: POS_STATUS[o.status] || '#555', background: '#F5F5F5', borderRadius: 20, padding: '1px 8px' }}>{o.status}</span>
                        </div>
                        <div style={{ fontSize: '.76rem', color: 'var(--muted)', marginBottom: 4 }}>
                          {(o.order_items || []).map(i => `${i.display_name} ×${i.qty}`).join(' · ')}
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ fontSize: '.72rem', color: 'var(--muted)' }}>{fmtDT(o.created_at)} · {o.payment}</span>
                          <span style={{ ...S.serif, fontSize: '.95rem', color: 'var(--brown)' }}>{fmtRp(o.total)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────
export default function AdminCustomers() {
  const [customers,    setCustomers]   = useState([]);
  const [orderCounts,  setOrderCounts] = useState({});
  const [lastProducts, setLastProducts]= useState({});
  const [filter,       setFilter]      = useState('all');
  const [search,       setSearch]      = useState('');
  const [sending,      setSending]     = useState({});
  const [selected,     setSelected]    = useState(null); // customer untuk drawer
  const [expireInfo,    setExpireInfo]   = useState(null); // { count, expireDays } setelah expire berjalan

  useEffect(() => { load(); }, []);

  async function load() {
    const { data: custs } = await supabase
      .from('customers')
      .select('id,name,wa,visit_count,last_seen_at,created_at,loyalty_tier,loyalty_points,loyalty_lifetime,notes,source')
      .order('last_seen_at', { ascending: false, nullsFirst: false });
    const custList = custs || [];
    setCustomers(custList);

    // ── Cek & expire poin yang sudah lama tidak aktif ──
    try {
      const loySettings = await getLoyaltySettings();
      if (loySettings.enabled && loySettings.expireDays > 0) {
        const expiredCount = await expireStalePoints(custList, loySettings);
        if (expiredCount > 0) {
          setExpireInfo({ count: expiredCount, expireDays: loySettings.expireDays });
        }
      }
    } catch (e) {
      console.warn('Expire points error (non-fatal):', e);
    }

    const { data: poOrders } = await supabase
      .from('po_orders')
      .select('customer_id, status, created_at, po:po_batches(product:products(name))')
      .neq('status', 'batal')
      .order('created_at', { ascending: false });

    const counts = {};
    const lastProd = {};
    (poOrders || []).forEach(o => {
      const cid = o.customer_id;
      if (!cid) return;
      counts[cid] = (counts[cid] || 0) + 1;
      if (!lastProd[cid]) lastProd[cid] = o.po?.product?.name || 'HAPin';
    });
    setOrderCounts(counts);
    setLastProducts(lastProd);
  }

  async function handleFollowUp(c, e) {
    e.stopPropagation();
    setSending(s => ({ ...s, [c.id]: true }));
    await sendFollowUpWA(c.wa, c.name, lastProducts[c.id] || 'HAPin');
    setSending(s => ({ ...s, [c.id]: false }));
  }

  const filtered = customers.filter(c => {
    const tag = getRetentionTag(c);
    if (filter === 'risk'   && !['Mulai jarang', 'Perlu follow-up'].includes(tag.label)) return false;
    if (filter === 'active' && tag.label !== 'Aktif') return false;
    if (search) {
      const q = search.toLowerCase();
      if (!c.name?.toLowerCase().includes(q) && !(c.wa || '').includes(q)) return false;
    }
    return true;
  });

  const riskCount = customers.filter(c =>
    ['Mulai jarang', 'Perlu follow-up'].includes(getRetentionTag(c).label)
  ).length;

  const FollowUpBtn = ({ c }) => {
    const tag = getRetentionTag(c);
    if (!['Mulai jarang', 'Perlu follow-up'].includes(tag.label)) return null;
    return (
      <button onClick={e => handleFollowUp(c, e)} disabled={sending[c.id]}
        style={{ padding: '4px 10px', borderRadius: 6, background: sending[c.id] ? '#EEE' : '#E8F5E9', border: '1px solid #A5D6A7', color: sending[c.id] ? '#999' : '#1B5E20', fontSize: '.72rem', whiteSpace: 'nowrap', cursor: 'pointer' }}>
        {sending[c.id] ? 'Mengirim...' : '📱 Follow-up'}
      </button>
    );
  };

  return (
    <div style={{ padding: 'clamp(16px,4vw,28px) clamp(12px,4vw,24px) 60px' }}>
      <style>{css}</style>

      {/* Drawer */}
      {selected && (
        <CustomerDrawer
          customer={selected}
          orderCounts={orderCounts}
          onClose={() => setSelected(null)}
        />
      )}

      {/* Header */}
      <div style={{ marginBottom: 18 }}>
        <div style={{ ...S.serif, fontSize: '1.5rem' }}>Customers</div>
        <div style={{ ...S.muted }}>{customers.length} customer · {riskCount} perlu follow-up</div>
      </div>

      {/* Banner expire */}
      {expireInfo && (
        <div style={{ background: '#FFF3E0', border: '1px solid #FFCC80', borderRadius: 10, padding: '10px 16px', marginBottom: 14, display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '.82rem' }}>
          <span>⏰ <strong>{expireInfo.count} customer</strong> poinnya baru di-expire karena tidak transaksi lebih dari <strong>{expireInfo.expireDays} hari</strong>.</span>
          <button onClick={() => setExpireInfo(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--muted)', fontSize: '.8rem' }}>✕</button>
        </div>
      )}

      {/* Filter + Search */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14, alignItems: 'center' }}>
        {[['all', 'Semua'], ['active', 'Aktif'], ['risk', 'Perlu Follow-up']].map(([v, l]) => (
          <button key={v} onClick={() => setFilter(v)}
            style={{ padding: '6px 14px', borderRadius: 20, border: '1px solid var(--border)', background: filter === v ? '#CD5226' : 'white', color: filter === v ? '#fff' : 'var(--muted)', fontSize: '.78rem', whiteSpace: 'nowrap', cursor: 'pointer' }}>
            {l}
          </button>
        ))}
        <input placeholder="Cari nama / WA..." value={search} onChange={e => setSearch(e.target.value)}
          style={{ marginLeft: 'auto', border: '1.5px solid #DDD', borderRadius: 8, padding: '6px 12px', fontSize: '.82rem', outline: 'none', minWidth: 160, maxWidth: 220 }}
          onFocus={e => e.target.style.borderColor = 'var(--gold)'}
          onBlur={e => e.target.style.borderColor = '#DDD'} />
      </div>

      {/* Desktop table */}
      <div className="cust-table" style={{ background: 'white', borderRadius: 'var(--radius)', border: '1px solid var(--border)', overflow: 'hidden', boxShadow: 'var(--shadow)' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '.82rem' }}>
          <thead>
            <tr>
              {['Nama', 'WhatsApp', 'Order', 'Poin', 'Tier', 'Terakhir', 'Retensi', 'Aksi'].map(h => (
                <th key={h} style={{ background: '#CD5226', color: '#fff', padding: '9px 14px', textAlign: 'left', fontSize: '.66rem', letterSpacing: '.8px', textTransform: 'uppercase', fontWeight: 500 }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={8} style={{ padding: '32px', textAlign: 'center', color: 'var(--muted)' }}>Tidak ada data</td></tr>
            ) : filtered.map(c => {
              const tag  = getRetentionTag(c);
              const ref  = c.last_seen_at || c.created_at;
              const days = daysSince(ref);
              const tier = TIER_COLOR[c.loyalty_tier] || TIER_COLOR.Kenalan;
              return (
                <tr key={c.id} className="cust-row" onClick={() => setSelected(c)}
                  style={{ borderBottom: '1px solid #F5EEE6' }}>
                  <td style={{ padding: '9px 14px' }}>
                    <div style={{ fontWeight: 600 }}>{c.name}</div>
                    <div style={{ fontSize: '.68rem', color: 'var(--muted)', marginTop: 1 }}>klik untuk detail</div>
                  </td>
                  <td style={{ padding: '9px 14px' }}>
                    <a href={`https://wa.me/62${(c.wa || '').replace(/^0/, '')}`}
                      target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()}
                      style={{ color: '#27AE60', ...S.mono, fontSize: '.76rem' }}>{c.wa}</a>
                  </td>
                  <td style={{ padding: '9px 14px' }}><strong>{orderCounts[c.id] || 0}</strong>×</td>
                  <td style={{ padding: '9px 14px' }}>
                    <div style={{ ...S.mono, fontSize: '.78rem', fontWeight: 600, color: 'var(--brown)' }}>
                      {(c.loyalty_points || 0).toLocaleString('id-ID')}
                    </div>
                    <div style={{ fontSize: '.66rem', color: 'var(--muted)' }}>
                      lifetime {(c.loyalty_lifetime || 0).toLocaleString('id-ID')}
                    </div>
                  </td>
                  <td style={{ padding: '9px 14px' }}>
                    <span style={{ ...S.mono, fontSize: '.66rem', background: tier.bg, color: tier.c, borderRadius: 20, padding: '2px 8px' }}>{c.loyalty_tier}</span>
                  </td>
                  <td style={{ padding: '9px 14px', fontSize: '.76rem', color: 'var(--muted)' }}>
                    {fmtDate(ref)}
                    {days < 999 && <div style={{ fontSize: '.68rem', color: days > 30 ? '#C62828' : 'var(--muted)' }}>{days}h lalu</div>}
                  </td>
                  <td style={{ padding: '9px 14px' }}>
                    <span style={{ background: tag.bg, color: tag.color, borderRadius: 20, padding: '2px 9px', fontSize: '.66rem', ...S.mono }}>{tag.label}</span>
                  </td>
                  <td style={{ padding: '9px 14px' }} onClick={e => e.stopPropagation()}>
                    <FollowUpBtn c={c} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <div className="cust-cards">
        {filtered.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 32, color: 'var(--muted)', fontSize: '.84rem' }}>Tidak ada data</div>
        ) : filtered.map(c => {
          const tag  = getRetentionTag(c);
          const ref  = c.last_seen_at || c.created_at;
          const days = daysSince(ref);
          const tier = TIER_COLOR[c.loyalty_tier] || TIER_COLOR.Kenalan;
          return (
            <div key={c.id} onClick={() => setSelected(c)}
              style={{ background: 'white', borderRadius: 'var(--radius)', border: '1px solid var(--border)', padding: '14px 16px', marginBottom: 10, boxShadow: 'var(--shadow)', cursor: 'pointer', active: { opacity: .85 } }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                <div>
                  <div style={{ fontWeight: 600, fontSize: '.9rem', marginBottom: 2 }}>{c.name}</div>
                  <a href={`https://wa.me/62${(c.wa || '').replace(/^0/, '')}`}
                    target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()}
                    style={{ color: '#27AE60', ...S.mono, fontSize: '.75rem' }}>{c.wa}</a>
                </div>
                <span style={{ background: tag.bg, color: tag.color, borderRadius: 20, padding: '2px 9px', fontSize: '.66rem', ...S.mono, whiteSpace: 'nowrap', marginLeft: 8 }}>{tag.label}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '.76rem', color: 'var(--muted)', flexWrap: 'wrap', gap: 6 }}>
                <span style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  <span>{orderCounts[c.id] || 0}× order</span>
                  <span>{days < 999 ? `${days}h lalu` : 'belum order'}</span>
                  <span style={{ ...S.mono, fontSize: '.66rem', background: tier.bg, color: tier.c, borderRadius: 20, padding: '1px 7px' }}>{c.loyalty_tier}</span>
                  {(c.loyalty_points || 0) > 0 && (
                    <span style={{ ...S.mono, fontSize: '.66rem', color: 'var(--brown)' }}>⭐ {c.loyalty_points} poin</span>
                  )}
                </span>
                <span onClick={e => e.stopPropagation()}>
                  <FollowUpBtn c={c} />
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
